/**
 * Repair Broken Markdown Tables
 *
 * Models often emit GFM tables that Streamdown cannot parse: headers fused into
 * a heading, orphaned separator rows, or status cells wrapped onto the next
 * line (`| todo |`). Normalize those shapes before markdown render.
 *
 * Primary responsibilities:
 * - Pull pipe-columns out of a fused heading into a real header row
 * - Join continuation cells onto the previous incomplete table row
 * - Leave valid tables untouched
 *
 * Dependencies:
 * - none
 */

const TABLE_SEPARATOR_LINE_REGEX =
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/

const TABLE_ROW_LINE_REGEX = /^\s*\|.*\|\s*$/

const HEADING_PREFIX_REGEX = /^\s{0,3}(#{1,6}\s+|>\s+|\*\*|__)/

const isTableSeparatorLine = (line: string) =>
  TABLE_SEPARATOR_LINE_REGEX.test(line)

const isTableRowLine = (line: string) =>
  TABLE_ROW_LINE_REGEX.test(line) && !isTableSeparatorLine(line)

const countColumns = (line: string) => {
  const trimmed = line.trim()
  if (!trimmed.includes('|')) return 0

  const cells = trimmed
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim())

  return cells.length
}

const splitPipeCells = (value: string) =>
  value
    .split('|')
    .map(cell => cell.trim())
    .filter((cell, index, cells) => {
      // Keep empty middle cells; drop leading/trailing empties from edge pipes.
      if (cell.length > 0) return true
      return index > 0 && index < cells.length - 1
    })

const toTableRow = (cells: string[]) =>
  `| ${cells.map(cell => cell || '—').join(' | ')} |`

const toSeparatorRow = (columnCount: number) =>
  `| ${Array.from({ length: Math.max(columnCount, 1) }, () => '---').join(' | ')} |`

/**
 * "📞 Priority Calls (Named Contacts + Verified Pain | Hook | Status |"
 * → title "📞 Priority Calls" + header cells from the pipe segment.
 */
const splitFusedHeadingAndHeader = (
  line: string
): { title: string | null; headerCells: string[] } | null => {
  if (isTableRowLine(line) || isTableSeparatorLine(line)) {
    return null
  }

  if (!line.includes('|')) {
    return null
  }

  const withoutHeadingMarks = line
    .replace(/^\s{0,3}#{1,6}\s+/, '')
    .replace(/^\s*>\s+/, '')
    .replace(/^\s*(\*\*|__)/, '')
    .replace(/(\*\*|__)\s*$/, '')
    .trim()

  const firstPipe = withoutHeadingMarks.indexOf('|')
  if (firstPipe <= 0) {
    // Starts with `|` — already a row-ish line; let other repairs handle it.
    return null
  }

  let title = withoutHeadingMarks.slice(0, firstPipe).trim()
  let pipeSegment = withoutHeadingMarks.slice(firstPipe)

  // "Title (Col A | Col B |" → title "Title", headers start at "Col A"
  const openParen = title.lastIndexOf('(')
  if (openParen >= 0) {
    const beforeParen = title.slice(0, openParen).trim()
    const afterParen = title.slice(openParen + 1).trim()
    if (beforeParen && afterParen) {
      title = beforeParen
      pipeSegment = `${afterParen} ${pipeSegment}`
    } else {
      title = title.replace(/\(\s*$/, '').trim()
    }
  } else {
    title = title.replace(/\(\s*$/, '').trim()
  }

  const headerCells = splitPipeCells(pipeSegment)
  if (headerCells.length < 2) {
    return null
  }

  return {
    title: title || null,
    headerCells
  }
}

const looksLikeContinuationRow = (line: string) => {
  const trimmed = line.trim()
  if (!trimmed.includes('|')) return false
  if (isTableSeparatorLine(trimmed)) return false

  // `| todo |` / `todo |` / `| todo`
  const cells = splitPipeCells(trimmed)
  if (cells.length === 0) return false
  if (cells.length > 3) return false

  return cells.every(
    cell =>
      cell.length <= 48 &&
      !cell.includes('http') &&
      !/^\[SPACE_ITEM_CARD\]/i.test(cell)
  )
}

const isIncompleteTableRow = (line: string, expectedColumns: number) => {
  if (!line.includes('|')) return false
  if (isTableSeparatorLine(line)) return false

  const trimmed = line.trim()
  const columns = countColumns(trimmed)

  // Missing trailing pipe or fewer cells than the separator advertised.
  if (!trimmed.endsWith('|') && trimmed.startsWith('|')) {
    return true
  }

  if (expectedColumns > 0 && columns > 0 && columns < expectedColumns) {
    return true
  }

  return false
}

const EMBEDDED_SEPARATOR_REGEX =
  /(\|\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?)/

/**
 * Models sometimes emit an entire table on one line:
 * `| H1 | H2 | |---|---| | r1 | r2 |`
 * Split that into real rows so the line-oriented repairs below can run.
 * Leave pure separator lines alone.
 */
const expandInlineTables = (content: string): string => {
  if (!content.includes('|---') && !content.includes('| ---')) {
    return content
  }

  return content
    .split('\n')
    .map(line => {
      const trimmed = line.trim()
      if (!trimmed.includes('|')) return line
      if (isTableSeparatorLine(trimmed)) return line

      const separatorMatch = trimmed.match(EMBEDDED_SEPARATOR_REGEX)
      if (!separatorMatch || separatorMatch.index == null) return line

      // Pure separator already returned above. Only expand when other cells
      // sit before and/or after the separator on the same line.
      const before = trimmed.slice(0, separatorMatch.index).trim()
      const separator = separatorMatch[1].trim()
      const after = trimmed
        .slice(separatorMatch.index + separatorMatch[1].length)
        .trim()

      if (!before && !after) return line

      return [before, separator, after].filter(Boolean).join('\n')
    })
    .join('\n')
}

/**
 * Normalize broken GFM tables in assistant markdown.
 */
export const repairBrokenMarkdownTables = (content: string): string => {
  if (!content || !content.includes('|')) {
    return content
  }

  const lines = expandInlineTables(content).split('\n')
  const output: string[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const next = lines[index + 1]

    // Case A: fused heading + separator on the next line.
    if (next && isTableSeparatorLine(next)) {
      const fused = splitFusedHeadingAndHeader(line)
      if (fused) {
        const separatorColumns = countColumns(next)
        const headerCells = [...fused.headerCells]

        while (headerCells.length < separatorColumns) {
          headerCells.push('—')
        }
        if (headerCells.length > separatorColumns && separatorColumns > 0) {
          headerCells.length = separatorColumns
        }

        if (fused.title) {
          const hadHeading = HEADING_PREFIX_REGEX.test(line.trim())
          output.push(hadHeading && line.trim().startsWith('#') ? line.replace(
            /^(\s{0,3}#{1,6}\s+).+$/,
            `$1${fused.title}`
          ) : fused.title)
          output.push('')
        }

        output.push(toTableRow(headerCells))
        output.push(
          separatorColumns > 0 ? next.trim() : toSeparatorRow(headerCells.length)
        )
        index += 2

        // Consume body rows, joining short continuations.
        const expectedColumns = Math.max(separatorColumns, headerCells.length)
        while (index < lines.length) {
          const bodyLine = lines[index]
          if (!bodyLine.trim()) break
          if (isTableSeparatorLine(bodyLine)) break

          const hasPipe = bodyLine.includes('|')
          if (!hasPipe && !bodyLine.trim().startsWith('|')) break

          // Not table-ish anymore (new heading / paragraph).
          if (
            !hasPipe &&
            (HEADING_PREFIX_REGEX.test(bodyLine) || !bodyLine.trim().startsWith('|'))
          ) {
            break
          }

          if (
            !isTableRowLine(bodyLine) &&
            !bodyLine.trim().startsWith('|') &&
            !looksLikeContinuationRow(bodyLine)
          ) {
            break
          }

          let row = bodyLine.trim()
          let columns = countColumns(row)

          // Join `| todo |` style continuations onto this row.
          while (
            index + 1 < lines.length &&
            isIncompleteTableRow(row, expectedColumns) &&
            looksLikeContinuationRow(lines[index + 1])
          ) {
            const continuationCells = splitPipeCells(lines[index + 1].trim())
            const rowCells = splitPipeCells(row)
            row = toTableRow([...rowCells, ...continuationCells])
            columns = countColumns(row)
            index += 1
          }

          // Ensure edge pipes for GFM.
          if (row.startsWith('|') && !row.endsWith('|')) {
            row = `${row} |`
          } else if (!row.startsWith('|') && row.includes('|')) {
            row = toTableRow(splitPipeCells(row))
          }

          // Pad short rows to separator width so the table still parses.
          if (expectedColumns > 0 && columns > 0 && columns < expectedColumns) {
            const cells = splitPipeCells(row)
            while (cells.length < expectedColumns) cells.push('—')
            row = toTableRow(cells)
          }

          output.push(row)
          index += 1
        }

        continue
      }
    }

    // Case B: valid header + separator, but body rows split across lines.
    // Also repair separators that advertise fewer columns than the header
    // (common model failure: `| a | b | c | d |` then `|---|---|`).
    if (
      isTableRowLine(line) &&
      next &&
      isTableSeparatorLine(next)
    ) {
      const headerColumns = countColumns(line)
      const separatorColumns = countColumns(next)
      const expectedColumns = Math.max(headerColumns, separatorColumns, 1)
      output.push(line)
      output.push(
        separatorColumns === expectedColumns
          ? next.trim()
          : toSeparatorRow(expectedColumns)
      )
      index += 2

      while (index < lines.length) {
        const bodyLine = lines[index]
        if (!bodyLine.trim()) break
        if (isTableSeparatorLine(bodyLine)) break
        if (
          !bodyLine.includes('|') &&
          !looksLikeContinuationRow(bodyLine)
        ) {
          break
        }
        if (
          !isTableRowLine(bodyLine) &&
          !bodyLine.trim().startsWith('|') &&
          !looksLikeContinuationRow(bodyLine)
        ) {
          break
        }

        let row = bodyLine.trim()
        while (
          index + 1 < lines.length &&
          isIncompleteTableRow(row, expectedColumns) &&
          looksLikeContinuationRow(lines[index + 1])
        ) {
          const continuationCells = splitPipeCells(lines[index + 1].trim())
          const rowCells = splitPipeCells(row)
          row = toTableRow([...rowCells, ...continuationCells])
          index += 1
        }

        if (row.startsWith('|') && !row.endsWith('|')) {
          row = `${row} |`
        }

        const columns = countColumns(row)
        if (expectedColumns > 0 && columns > 0 && columns < expectedColumns) {
          const cells = splitPipeCells(row)
          while (cells.length < expectedColumns) cells.push('—')
          row = toTableRow(cells)
        }

        output.push(row)
        index += 1
      }

      continue
    }

    // Case C: orphaned separator with no usable header above — invent a blank header.
    if (
      isTableSeparatorLine(line) &&
      (!output.length || !isTableRowLine(output[output.length - 1] || ''))
    ) {
      const columnCount = countColumns(line)
      const blankHeader = Array.from({ length: Math.max(columnCount, 1) }, (_, i) =>
        `Column ${i + 1}`
      )
      output.push(toTableRow(blankHeader))
      output.push(line.trim())
      index += 1
      continue
    }

    output.push(line)
    index += 1
  }

  return output.join('\n')
}
